import React, { useState, useRef } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Camera, Upload, Trash2, Loader2 } from 'lucide-react';
import { getAthleteFullName, getAthleteInitials } from '@/utils/athleteUtils';

interface AthletePhotoUploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  athlete: {
    id: string;
    first_name?: string | null;
    last_name?: string | null;
    name: string;
    photo_url?: string | null;
    current_team_name?: string | null;
    sport: string;
  } | null;
  onSuccess: () => void;
}

export const AthletePhotoUploadDialog: React.FC<AthletePhotoUploadDialogProps> = ({
  open,
  onOpenChange,
  athlete,
  onSuccess,
}) => {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(false);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast({
        title: 'Invalid file type',
        description: 'Please select an image file (JPG, PNG, etc.)',
        variant: 'destructive',
      });
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: 'File too large',
        description: 'Please select an image smaller than 5MB',
        variant: 'destructive',
      });
      return;
    }

    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const handleUpload = async () => {
    if (!selectedFile || !athlete) return;

    setUploading(true);
    try {
      // Generate a unique filename
      const fileExt = selectedFile.name.split('.').pop();
      const fileName = `${athlete.id}-${Date.now()}.${fileExt}`;
      const filePath = `${athlete.sport}/${fileName}`;

      // Upload to storage
      const { error: uploadError } = await supabase.storage
        .from('athlete-photos')
        .upload(filePath, selectedFile, { upsert: true });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('athlete-photos')
        .getPublicUrl(filePath);

      // Update athlete record
      const { error: updateError } = await supabase
        .from('api_athletes')
        .update({ photo_url: urlData.publicUrl })
        .eq('id', athlete.id);

      if (updateError) throw updateError;

      toast({
        title: 'Photo uploaded',
        description: `Successfully updated photo for ${getAthleteFullName(athlete)}`,
      });

      onSuccess();
      handleClose();
    } catch (error: any) {
      console.error('Upload error:', error);
      toast({
        title: 'Upload failed',
        description: error.message || 'Failed to upload photo',
        variant: 'destructive',
      });
    } finally {
      setUploading(false);
    }
  };

  const handleRemovePhoto = async () => {
    if (!athlete?.photo_url) return;

    setRemoving(true);
    try {
      // Extract file path from URL if it's from our storage
      const url = athlete.photo_url;
      if (url.includes('athlete-photos')) {
        const pathMatch = url.match(/athlete-photos\/(.+)$/);
        if (pathMatch) {
          await supabase.storage
            .from('athlete-photos')
            .remove([pathMatch[1]]);
        }
      }

      // Update athlete record to remove photo URL
      const { error: updateError } = await supabase
        .from('api_athletes')
        .update({ photo_url: null })
        .eq('id', athlete.id);

      if (updateError) throw updateError;

      toast({
        title: 'Photo removed',
        description: `Successfully removed photo for ${getAthleteFullName(athlete)}`,
      });

      onSuccess();
      handleClose();
    } catch (error: any) {
      console.error('Remove error:', error);
      toast({
        title: 'Remove failed',
        description: error.message || 'Failed to remove photo',
        variant: 'destructive',
      });
    } finally {
      setRemoving(false);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onOpenChange(false);
  };

  if (!athlete) return null;

  const currentPhotoUrl = previewUrl || athlete.photo_url;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Update Athlete Photo</DialogTitle>
          <DialogDescription>
            Upload a photo for {getAthleteFullName(athlete)} ({athlete.current_team_name || athlete.sport})
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Current/Preview Photo */}
          <div className="flex flex-col items-center gap-4">
            <Avatar className="h-32 w-32">
              <AvatarImage src={currentPhotoUrl || undefined} />
              <AvatarFallback className="text-2xl">
                {getAthleteInitials(athlete)}
              </AvatarFallback>
            </Avatar>
            
            {currentPhotoUrl && !previewUrl && athlete.photo_url && (
              <p className="text-xs text-muted-foreground">Current photo</p>
            )}
            {previewUrl && (
              <p className="text-xs text-green-600">New photo preview</p>
            )}
          </div>

          {/* File Input */}
          <div className="space-y-2">
            <Label htmlFor="photo-upload">Select Image</Label>
            <input
              ref={fileInputRef}
              id="photo-upload"
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              className="hidden"
            />
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={() => fileInputRef.current?.click()}
            >
              <Camera className="h-4 w-4 mr-2" />
              {selectedFile ? 'Change Image' : 'Choose Image'}
            </Button>
            {selectedFile && (
              <p className="text-xs text-muted-foreground text-center">
                {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
              </p>
            )}
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          {athlete.photo_url && !previewUrl && (
            <Button
              type="button"
              variant="destructive"
              onClick={handleRemovePhoto}
              disabled={removing || uploading}
              className="w-full sm:w-auto"
            >
              {removing ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4 mr-2" />
              )}
              Remove Photo
            </Button>
          )}
          <div className="flex gap-2 w-full sm:w-auto">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={uploading || removing}
              className="flex-1 sm:flex-initial"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleUpload}
              disabled={!selectedFile || uploading || removing}
              className="flex-1 sm:flex-initial"
            >
              {uploading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Upload className="h-4 w-4 mr-2" />
              )}
              Upload
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
